/**
 * Predictive Failure Intelligence — Detect patterns before they become emergencies.
 *
 * Pure functions that analyze work order history to detect:
 * - Recurring issues at the same unit (the "fix it once" failure)
 * - Cross-property patterns (e.g., same plumbing issue in all units with same fixtures)
 * - Seasonal patterns (HVAC issues spike in summer/winter)
 * - Failure probability scoring for proactive maintenance
 *
 * Data model expectations:
 * - Work orders: { id, unitId, propertyId, description, status, createdDate, category }
 */

import { isActiveWO } from './dataQueries';

// ─── Issue Similarity ────────────────────────────────────────────────────────

/**
 * Extract normalized keywords from a WO description for comparison.
 * Strips common words, lowercases, and returns unique terms.
 */
const STOP_WORDS = new Set([
  'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'in', 'on', 'at', 'to', 'for', 'of', 'with', 'and', 'or', 'not',
  'it', 'this', 'that', 'has', 'have', 'had', 'do', 'does', 'did',
  'will', 'would', 'could', 'should', 'may', 'might', 'can',
  'please', 'need', 'needs', 'unit', 'tenant', 'resident', 'says',
]);

export function extractKeywords(description) {
  if (!description || typeof description !== 'string') return [];

  return description
    .toLowerCase()
    .replace(/[^a-z0-9\s/-]/g, '')
    .split(/\s+/)
    .filter(w => w.length > 2 && !STOP_WORDS.has(w))
    .filter((w, i, arr) => arr.indexOf(w) === i); // unique
}

/**
 * Calculate similarity between two WO descriptions (0-1 Jaccard index).
 */
export function calculateSimilarity(desc1, desc2) {
  const kw1 = new Set(extractKeywords(desc1));
  const kw2 = new Set(extractKeywords(desc2));

  if (kw1.size === 0 || kw2.size === 0) return 0;

  const intersection = new Set([...kw1].filter(x => kw2.has(x)));
  const union = new Set([...kw1, ...kw2]);

  return intersection.size / union.size;
}

// ─── Recurring Issue Detection ───────────────────────────────────────────────

/**
 * Detect recurring issues at a specific unit.
 * Returns groups of similar WOs that indicate the same problem keeps coming back.
 *
 * @param {Array} workOrders - All work orders
 * @param {string} unitId - Unit to analyze
 * @param {number} [similarityThreshold=0.4] - Min similarity to consider same issue
 * @param {number} [minOccurrences=2] - Min times to flag as recurring
 * @returns {Array} Recurring issue groups: { description, occurrences, firstSeen, lastSeen, woIds }
 */
export function detectRecurringIssues(workOrders, unitId, similarityThreshold = 0.4, minOccurrences = 2) {
  if (!Array.isArray(workOrders) || !unitId) return [];

  const unitWOs = workOrders
    .filter(wo => wo.unitId === unitId && wo.description)
    .sort((a, b) => new Date(a.createdDate || 0) - new Date(b.createdDate || 0));

  if (unitWOs.length < minOccurrences) return [];

  // Group similar WOs together
  const groups = [];
  const assigned = new Set();

  for (let i = 0; i < unitWOs.length; i++) {
    if (assigned.has(i)) continue;

    const group = [unitWOs[i]];
    assigned.add(i);

    for (let j = i + 1; j < unitWOs.length; j++) {
      if (assigned.has(j)) continue;

      const sim = calculateSimilarity(unitWOs[i].description, unitWOs[j].description);
      if (sim >= similarityThreshold) {
        group.push(unitWOs[j]);
        assigned.add(j);
      }
    }

    if (group.length >= minOccurrences) {
      groups.push({
        description: group[0].description,
        keywords: extractKeywords(group[0].description),
        occurrences: group.length,
        firstSeen: group[0].createdDate,
        lastSeen: group[group.length - 1].createdDate,
        woIds: group.map(wo => wo.id),
        statuses: group.map(wo => wo.status),
        stillOpen: group.some(wo => isActiveWO(wo)),
      });
    }
  }

  return groups.sort((a, b) => b.occurrences - a.occurrences);
}

// ─── Cross-Property Pattern Detection ────────────────────────────────────────

/**
 * Find similar issues occurring across multiple units in a property.
 * Indicates systemic problems (e.g., all 2nd-floor units have the same leak).
 */
export function findCrossPropertyPatterns(workOrders, propertyId, similarityThreshold = 0.4, minUnits = 2) {
  if (!Array.isArray(workOrders) || !propertyId) return [];

  const propertyWOs = workOrders.filter(wo =>
    wo.propertyId === propertyId && wo.description
  );

  if (propertyWOs.length < minUnits) return [];

  // Group by similarity, then check how many unique units are affected
  const patterns = [];
  const assigned = new Set();

  for (let i = 0; i < propertyWOs.length; i++) {
    if (assigned.has(i)) continue;

    const group = [propertyWOs[i]];
    assigned.add(i);

    for (let j = i + 1; j < propertyWOs.length; j++) {
      if (assigned.has(j)) continue;

      const sim = calculateSimilarity(propertyWOs[i].description, propertyWOs[j].description);
      if (sim >= similarityThreshold) {
        group.push(propertyWOs[j]);
        assigned.add(j);
      }
    }

    const affectedUnits = new Set(group.map(wo => wo.unitId).filter(Boolean));

    if (affectedUnits.size >= minUnits) {
      patterns.push({
        description: group[0].description,
        keywords: extractKeywords(group[0].description),
        totalOccurrences: group.length,
        affectedUnits: [...affectedUnits],
        unitCount: affectedUnits.size,
        firstSeen: group.reduce((min, wo) => !min || wo.createdDate < min ? wo.createdDate : min, null),
        lastSeen: group.reduce((max, wo) => !max || wo.createdDate > max ? wo.createdDate : max, null),
        woIds: group.map(wo => wo.id),
      });
    }
  }

  return patterns.sort((a, b) => b.unitCount - a.unitCount || b.totalOccurrences - a.totalOccurrences);
}

// ─── Seasonal Pattern Detection ──────────────────────────────────────────────

/**
 * Analyze WO creation by month to find seasonal spikes.
 * Returns month-by-month counts with deviation from average.
 */
export function analyzeSeasonalPatterns(workOrders, propertyId = null) {
  if (!Array.isArray(workOrders)) return [];

  const filtered = propertyId
    ? workOrders.filter(wo => wo.propertyId === propertyId)
    : workOrders;

  // Count by month (0-11)
  const monthCounts = new Array(12).fill(0);
  for (const wo of filtered) {
    if (!wo.createdDate) continue;
    const month = new Date(wo.createdDate).getMonth();
    if (!isNaN(month)) monthCounts[month]++;
  }

  const total = monthCounts.reduce((s, c) => s + c, 0);
  if (total === 0) return [];

  const average = total / 12;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return monthCounts.map((count, i) => ({
    month: i,
    monthName: monthNames[i],
    count,
    deviation: average > 0 ? ((count - average) / average) : 0,
    isSpike: count > average * 1.5,
  }));
}

// ─── Failure Probability ─────────────────────────────────────────────────────

/**
 * Calculate failure probability score for a unit (0-100).
 * Higher = more likely to need maintenance soon.
 *
 * Factors:
 * - Frequency of WOs (more = higher risk)
 * - Recency of last WO (recent = higher risk)
 * - Recurring issues (unresolved patterns)
 * - Open WOs (already has problems)
 */
export function calculateFailureProbability(workOrders, unitId) {
  if (!Array.isArray(workOrders) || !unitId) return { score: 0, factors: [] };

  const unitWOs = workOrders.filter(wo => wo.unitId === unitId);
  if (unitWOs.length === 0) return { score: 0, factors: [] };

  const factors = [];
  let score = 0;

  // Factor 1: WO frequency (0-30 points)
  const frequencyScore = Math.min(30, unitWOs.length * 5);
  if (frequencyScore > 15) {
    factors.push({ factor: 'High WO frequency', impact: frequencyScore, detail: `${unitWOs.length} work orders on file` });
  }
  score += frequencyScore;

  // Factor 2: Recency (0-25 points)
  const sorted = unitWOs.sort((a, b) => new Date(b.createdDate || 0) - new Date(a.createdDate || 0));
  const lastWO = sorted[0];
  if (lastWO?.createdDate) {
    const daysSince = (Date.now() - new Date(lastWO.createdDate).getTime()) / (1000 * 60 * 60 * 24);
    const recencyScore = daysSince < 30 ? 25 : daysSince < 90 ? 15 : daysSince < 180 ? 8 : 0;
    if (recencyScore > 0) {
      factors.push({ factor: 'Recent work order', impact: recencyScore, detail: `Last WO ${Math.round(daysSince)} days ago` });
    }
    score += recencyScore;
  }

  // Factor 3: Open WOs (0-25 points)
  const openCount = unitWOs.filter(wo => isActiveWO(wo)).length;
  const openScore = Math.min(25, openCount * 12);
  if (openScore > 0) {
    factors.push({ factor: 'Open work orders', impact: openScore, detail: `${openCount} currently open` });
  }
  score += openScore;

  // Factor 4: Recurring issues (0-20 points)
  const recurring = detectRecurringIssues(workOrders, unitId);
  const recurringScore = Math.min(20, recurring.length * 10);
  if (recurringScore > 0) {
    factors.push({ factor: 'Recurring issues', impact: recurringScore, detail: `${recurring.length} recurring patterns` });
  }
  score += recurringScore;

  return {
    score: Math.min(100, score),
    level: score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low',
    factors,
    totalWOs: unitWOs.length,
    openWOs: openCount,
    recurringIssues: recurring.length,
  };
}

// ─── Property Health Score ───────────────────────────────────────────────────

/**
 * Calculate overall health score for a property based on all its units.
 * Returns 0-100 where 100 = perfect health (no issues).
 */
export function getPropertyHealthScore(workOrders, propertyId, units) {
  if (!Array.isArray(workOrders) || !propertyId) return { score: 100, details: {} };

  const propertyUnits = Array.isArray(units)
    ? units.filter(u => u.propertyId === propertyId)
    : [];

  const propertyWOs = workOrders.filter(wo => wo.propertyId === propertyId);
  if (propertyWOs.length === 0) return { score: 100, details: { reason: 'No work orders on file' } };

  const totalUnits = propertyUnits.length || 1;

  // Metrics
  const openWOs = propertyWOs.filter(wo => isActiveWO(wo)).length;
  const crossPatterns = findCrossPropertyPatterns(workOrders, propertyId);
  const unitsWithRecurring = new Set();

  for (const u of propertyUnits) {
    const recurring = detectRecurringIssues(workOrders, u.unitId || u.id);
    if (recurring.length > 0) unitsWithRecurring.add(u.unitId || u.id);
  }

  // Score deductions from 100
  let deductions = 0;
  deductions += Math.min(30, openWOs * 5);                    // Open WOs
  deductions += Math.min(25, crossPatterns.length * 12);       // Cross-property issues
  deductions += Math.min(25, unitsWithRecurring.size * 8);     // Recurring issues
  deductions += Math.min(20, (propertyWOs.length / totalUnits) * 2); // WO density

  return {
    score: Math.max(0, Math.round(100 - deductions)),
    details: {
      totalWOs: propertyWOs.length,
      openWOs,
      crossPropertyPatterns: crossPatterns.length,
      unitsWithRecurringIssues: unitsWithRecurring.size,
      totalUnits,
      woDensity: Math.round((propertyWOs.length / totalUnits) * 10) / 10,
    },
  };
}
